import { combineReducers, Reducer } from "redux";
import { CLEAR_LOCAL_STATE } from "constants/actionTypes";
import { clearState } from "store/persistConfig";

// Import all reducers
import ledgerAccounts from "reducers/ledgerAccounts";
import masterLedgerAccounts from "reducers/masterLedgerAccounts";
import sundryTemplates from "reducers/sundryTemplates";
import { masterRuleSetReducer as masterRuleSets } from "reducers/masterRuleSets";
import agencies from "./agencies";
import auctionPens from "./auctionPens";
import auditLog from "./auditLog";
import auth from "./auth";
import bidderRegistrations from "./bidderRegistrations";
import billingDocumentActivity from "./billingDocumentActivity";
import billingDocuments from "./billingDocuments";
import billingRuns from "./billingRuns";
import billingTags from "./billingTags";
import billingSettings from "./billingSettings";
import businessesV2 from "./businessesV2";
import businessMergeEvents from "./businessMergeEvents";
import businessRelations from "./businessRelations";
import buyerSplits from "./buyerSplits";
import candidateDeclarations from "./candidateDeclarations";
import carrierCharges from "./carrierCharges";
import checkpoints from "./checkpoints";
import comments from "./comments";
import consignments from "./consignments";
import deploymentAttributes from "./deploymentAttributes";
import deployments from "./deployments";
import draftingInformation from "./draftingInformation";
import dressingRanges from "./dressingRanges";
import eContracts from "./eContracts";
import email from "./email";
import envdDocuments from "./envd";
import files from "./files";
import globalBusinesses from "./globalBusinesses";
import importers from "./importers";
import integrationBusinesses from "./integrationBusinesses";
import integrationCredentials from "./integrationCredentials";
import interestSettings from "./interest";
import ledgerEntries from "./ledgerEntries";
import sideLoadedBusinesses from "./sideLoadedBusinesses";
import livestockAgents from "./livestockAgents";
import loads from "./loads";
import manualAdjustments from "./manualAdjustments";
import masterLabels from "./masterLabels";
import mlaSupplementaryData from "./mlaSupplementaryData";
import modals from "./modals";
import nlisFiles from "./nlisFiles";
import nlisMessageGroups from "./nlisMessageGroups";
import nominations from "./nominations";
import nominationTerms from "./nominationTerms";
import offlineTemp from "./offlineTemp";
import payments from "./payments";
import penArchetypes from "./penArchetypes";
import penScanLots from "./penScanLots";
import permissionGroups from "./permissionGroups";
import products from "./products";
import properties from "./properties";
import receivalLots from "./receivalLots";
import reportJobs from "./reportJobs";
import reports from "./reports";
import {
  masterRuleBookReducer as masterRuleBooks,
  ruleBookReducer as ruleBooks,
} from "./ruleBooks";
import {
  masterRuleReducer as masterRules,
  ruleReducer as rules,
} from "./rules";
import { ruleSetReducer as ruleSets } from "./ruleSets";
import saleDefinitions from "./saleDefinitions";
import saleLots from "./saleLots";
import sales from "./sales";
import saleSubTypes from "./saleSubTypes";
import saleStatus from "./saleStatus";
import saleyardAdmins from "./saleyardAdmins";
import saleyards from "./saleyards";
import savedViews from "./savedViews";
import scanners from "./scanners";
import screen from "./screen";
import settings from "./settings";
import singleWeighs from "./singleWeighs";
import socket from "./socket";
import speciesAttributes from "./speciesAttributes";
import synced from "./synced";
import system from "./system";
import tradingTerms from "./tradingTerms";
import userLevels from "./userLevels";
import vendorCommissionBands from "./vendorCommissionBands";
import vendorSplits from "./vendorSplits";
import weighLots from "./weighLots";
import weighLotScans from "./weighLotScans";
import weightRanges from "./weightRanges";
import businessImportMappings from "./businessImportMappings";
import xeroTrackingCategories from "./xeroTrackingCategories";

// Define the shape of the root state
export interface RootReducersState {
  agencies: ReturnType<typeof agencies>;
  auctionPens: ReturnType<typeof auctionPens>;
  auditLog: ReturnType<typeof auditLog>;
  auth: ReturnType<typeof auth>;
  bidderRegistrations: ReturnType<typeof bidderRegistrations>;
  billingDocumentActivity: ReturnType<typeof billingDocumentActivity>;
  billingDocuments: ReturnType<typeof billingDocuments>;
  billingRuns: ReturnType<typeof billingRuns>;
  billingSettings: ReturnType<typeof billingSettings>;
  billingTags: ReturnType<typeof billingTags>;
  businessMergeEvents: ReturnType<typeof businessMergeEvents>;
  businessRelations: ReturnType<typeof businessRelations>;
  businessesV2: ReturnType<typeof businessesV2>;
  buyerSplits: ReturnType<typeof buyerSplits>;
  candidateDeclarations: ReturnType<typeof candidateDeclarations>;
  carrierCharges: ReturnType<typeof carrierCharges>;
  checkpoints: ReturnType<typeof checkpoints>;
  comments: ReturnType<typeof comments>;
  consignments: ReturnType<typeof consignments>;
  deploymentAttributes: ReturnType<typeof deploymentAttributes>;
  deployments: ReturnType<typeof deployments>;
  draftingInformation: ReturnType<typeof draftingInformation>;
  dressingRanges: ReturnType<typeof dressingRanges>;
  eContracts: ReturnType<typeof eContracts>;
  email: ReturnType<typeof email>;
  envdDocuments: ReturnType<typeof envdDocuments>;
  files: ReturnType<typeof files>;
  globalBusinesses: ReturnType<typeof globalBusinesses>;
  importers: ReturnType<typeof importers>;
  integrationBusinesses: ReturnType<typeof integrationBusinesses>;
  integrationCredentials: ReturnType<typeof integrationCredentials>;
  interestSettings: ReturnType<typeof interestSettings>;
  ledgerAccounts: ReturnType<typeof ledgerAccounts>;
  ledgerEntries: ReturnType<typeof ledgerEntries>;
  livestockAgents: ReturnType<typeof livestockAgents>;
  loads: ReturnType<typeof loads>;
  manualAdjustments: ReturnType<typeof manualAdjustments>;
  masterLabels: ReturnType<typeof masterLabels>;
  masterLedgerAccounts: ReturnType<typeof masterLedgerAccounts>;
  masterRuleBooks: ReturnType<typeof masterRuleBooks>;
  masterRuleSets: ReturnType<typeof masterRuleSets>;
  masterRules: ReturnType<typeof masterRules>;
  mlaSupplementaryData: ReturnType<typeof mlaSupplementaryData>;
  modals: ReturnType<typeof modals>;
  nlisFiles: ReturnType<typeof nlisFiles>;
  nlisMessageGroups: ReturnType<typeof nlisMessageGroups>;
  nominationTerms: ReturnType<typeof nominationTerms>;
  nominations: ReturnType<typeof nominations>;
  offlineTemp: ReturnType<typeof offlineTemp>;
  payments: ReturnType<typeof payments>;
  penArchetypes: ReturnType<typeof penArchetypes>;
  penScanLots: ReturnType<typeof penScanLots>;
  permissionGroups: ReturnType<typeof permissionGroups>;
  products: ReturnType<typeof products>;
  properties: ReturnType<typeof properties>;
  receivalLots: ReturnType<typeof receivalLots>;
  reportJobs: ReturnType<typeof reportJobs>;
  reports: ReturnType<typeof reports>;
  ruleBooks: ReturnType<typeof ruleBooks>;
  ruleSets: ReturnType<typeof ruleSets>;
  rules: ReturnType<typeof rules>;
  saleDefinitions: ReturnType<typeof saleDefinitions>;
  saleLots: ReturnType<typeof saleLots>;
  saleSubTypes: ReturnType<typeof saleSubTypes>;
  saleStatus: ReturnType<typeof saleStatus>;
  sales: ReturnType<typeof sales>;
  saleyardAdmins: ReturnType<typeof saleyardAdmins>;
  saleyards: ReturnType<typeof saleyards>;
  savedViews: ReturnType<typeof savedViews>;
  scanners: ReturnType<typeof scanners>;
  screen: ReturnType<typeof screen>;
  settings: ReturnType<typeof settings>;
  sideLoadedBusinesses: ReturnType<typeof sideLoadedBusinesses>;
  singleWeighs: ReturnType<typeof singleWeighs>;
  socket: ReturnType<typeof socket>;
  speciesAttributes: ReturnType<typeof speciesAttributes>;
  sundryTemplates: ReturnType<typeof sundryTemplates>;
  synced: ReturnType<typeof synced>;
  system: ReturnType<typeof system>;
  tradingTerms: ReturnType<typeof tradingTerms>;
  userLevels: ReturnType<typeof userLevels>;
  vendorCommissionBands: ReturnType<typeof vendorCommissionBands>;
  vendorSplits: ReturnType<typeof vendorSplits>;
  weighLotScans: ReturnType<typeof weighLotScans>;
  weighLots: ReturnType<typeof weighLots>;
  weightRanges: ReturnType<typeof weightRanges>;
  businessImportMappings: ReturnType<typeof businessImportMappings>;
  xeroTrackingCategories: ReturnType<typeof xeroTrackingCategories>;
}

// Combine all reducers
const appReducer = combineReducers<RootReducersState>({
  agencies,
  auctionPens,
  auditLog,
  auth,
  bidderRegistrations,
  billingDocumentActivity,
  billingDocuments,
  billingRuns,
  billingSettings,
  billingTags,
  businessesV2,
  businessMergeEvents,
  businessRelations,
  buyerSplits,
  candidateDeclarations,
  carrierCharges,
  checkpoints,
  comments,
  consignments,
  deploymentAttributes,
  deployments,
  draftingInformation,
  dressingRanges,
  eContracts,
  email,
  envdDocuments,
  files,
  globalBusinesses,
  importers,
  integrationBusinesses,
  integrationCredentials,
  interestSettings,
  ledgerAccounts,
  ledgerEntries,
  livestockAgents,
  loads,
  manualAdjustments,
  masterLabels,
  masterLedgerAccounts,
  masterRuleBooks,
  masterRuleSets,
  masterRules,
  mlaSupplementaryData,
  modals,
  nlisFiles,
  nlisMessageGroups,
  nominationTerms,
  nominations,
  offlineTemp,
  payments,
  penArchetypes,
  penScanLots,
  permissionGroups,
  products,
  properties,
  receivalLots,
  reportJobs,
  reports,
  ruleBooks,
  ruleSets,
  rules,
  saleDefinitions,
  saleLots,
  saleSubTypes,
  saleStatus,
  sales,
  saleyardAdmins,
  saleyards,
  savedViews,
  scanners,
  screen,
  settings,
  singleWeighs,
  sideLoadedBusinesses,
  socket,
  speciesAttributes,
  sundryTemplates,
  synced,
  system,
  tradingTerms,
  userLevels,
  vendorCommissionBands,
  vendorSplits,
  weighLotScans,
  weighLots,
  weightRanges,
  businessImportMappings,
  xeroTrackingCategories,
});

// Root reducer with CLEAR_LOCAL_STATE handling
const rootReducer: Reducer<RootState> = (state, action) => {
  // Clear out this data on failure or success - just AFTER trying to tell the server.
  if (action.type === CLEAR_LOCAL_STATE) {
    // Local Storage wipe
    clearState();
    sessionStorage.clear();
    // Service worker hose
    try {
      caches.keys().then(names => {
        for (const name of names) {
          caches.delete(name);
        }
      });
    } catch (e) {
      /* do nothing */
    }
    state = undefined;
  }

  return appReducer(state, action);
};

export default rootReducer;
